import React from 'react';
import type { ReactNode } from 'react';

import styled from 'styled-components';

import {
  colors,
  padding,
  fontWeight,
} from '@grnhse/seedling/lib/azalea/constants';
import {
  PencilIcon,
  GrippyIcon,
  XIcon,
} from '@grnhse/seedling/lib/azalea/icons';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'shared/utils/button'. 'app/webpack/javascripts/shared/utils/button.js' implicitly has an 'any' type.
import { clickOnKeyUp } from 'shared/utils/button';
import { withLocalePrefix } from 'shared/utils/translation';
const t = withLocalePrefix('job_kickoff.form_configuration');

const noop = () => {};

type Props = {
  children?: ReactNode;
  className?: string;
  style?: any;
  icon?: ReactNode;
  dataName?: string;
  maxLength?: number;
  text?: string;
  linkedField?: any;
  item: any;
  onEditRowClick: (arg1: any) => void;
  onRemoveRowClick: (arg1: any) => void;
  onRowChanged: (arg1: any) => void;
  displayRowText?: () => string;
  draggable: boolean;
  editable: boolean;
  removable: boolean;
  iconPosition: 'left' | 'right';
  maxHeight: string | null | undefined;
};

export default class EditableRow extends React.Component<Props> {
  static defaultProps = {
    onEditRowClick: noop,
    onRemoveRowClick: noop,
    onRowChanged: noop,
    draggable: true,
    editable: false,
    removable: false,
    iconPosition: 'left',
    maxHeight: '23px',
  };

  onRemove = () => {
    this.props.onRemoveRowClick(this.props.item);
  };

  onEdit = () => {
    this.props.onEditRowClick(this.props.item);
  };

  render() {
    const {
      children,
      className,
      dataName,
      style,
      icon,
      displayRowText,
      linkedField,
      draggable,
      editable,
      removable,
      iconPosition,
      maxHeight,
    } = this.props;

    const displayText = (displayRowText || (() => children))();

    return (
      <RowWrapper className={className} style={style}>
        {draggable && (
          <DraggableIndicator>
            <GrippyIcon />
          </DraggableIndicator>
        )}

        <RowContent>
          <IconAndFieldName maxHeight={maxHeight}>
            {iconPosition === 'left' && icon}

            {/* @ts-expect-error - TS2769 - No overload matches this call. */}
            <RowNameContainer hasIcon={iconPosition === 'right'}>
              <RowText data-test={'question-text'}>{displayText}</RowText>
              {linkedField}
              {iconPosition === 'right' && icon}
            </RowNameContainer>

            <ControlsBar>
              <EditableControlsContainer
                className="controls"
                data-test-name={displayText}
              >
                {editable && (
                  <ControlButton
                    className="control edit"
                    onClick={this.onEdit}
                    data-test-name={displayText}
                  >
                    <PencilIcon title={t('edit')} width="18px" height="18px" />
                  </ControlButton>
                )}
                {removable && (
                  <RemoveButton
                    className="control remove"
                    onClick={this.onRemove}
                    onKeyUp={clickOnKeyUp}
                    // @ts-expect-error - TS2769 - No overload matches this call.
                    tabIndex="0"
                    // $FlowFixMe
                    data-provides={`remove-${dataName}-editable-row`}
                  >
                    <StyledXIcon />
                  </RemoveButton>
                )}
              </EditableControlsContainer>
            </ControlsBar>
          </IconAndFieldName>
        </RowContent>
      </RowWrapper>
    );
  }
}

const controlsBarWidth = 60;

// The 26 pixels helps center the icon, the real size is dictated by the width
const StyledXIcon = styled(XIcon)`
  width: 20px;
  height: 26px;
`;

const RowWrapper = styled.div`
  position: relative;

  padding: ${padding.small};
  margin: ${padding.tiny} 0;

  color: ${colors.darkestGrey};
  background: ${colors.lightestGrey};

  border: 1px solid ${colors.lightGrey};

  & > div {
    vertical-align: middle;
  }
`;

const RowText = styled.p`
  line-height: 22px;
  font-size: 14px;
  font-weight: ${fontWeight.normal};
  color: #222222;
  box-sizing: border-box;
  display: inline-block;
  margin-right: ${padding.tiny};

  overflow-wrap: break-word;
  word-wrap: break-word;

  // to preserve line breaks but collapse white-space
  white-space: pre-line;
`;

const draggableIconWidth = '20px';
const DraggableIndicator = styled.div`
  display: inline-block;
  margin-right: ${padding.small};
  width: ${draggableIconWidth};

  svg {
    vertical-align: middle;
  }
`;

const RowContent = styled.div`
  display: inline-block;
  width: calc(100% - ${padding.small} - ${draggableIconWidth});
`;

const IconAndFieldName = styled.div<Pick<Props, 'maxHeight'>>`
  max-height: ${(props) => props.maxHeight};
  display: flex;
  align-items: center;
`;

const RowNameContainer = styled.div`
  width: calc(100% - 120px);
  ${(props) =>
    // @ts-expect-error - TS2339 - Property 'hasIcon' does not exist on type 'ThemedStyledProps<Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "key" | keyof HTMLAttributes<HTMLDivElement>> & { ...; }, any>'.
    props.hasIcon &&
    `
    display: flex;
    align-items: center;
  `}
`;

const editableControlsWidth = '55px';
const ControlsBar = styled.span`
  position: absolute;
  display: inline-block;
  right: ${padding.normal};
  width: ${controlsBarWidth}px;

  text-align: right;

  .controls {
    vertical-align: text-top;
  }

  .controls + .controls {
    margin-left: ${padding.small};
  }

  &.read-only {
    padding-right: calc(${editableControlsWidth} + ${padding.small});
  }
`;

const EditableControlsContainer = styled.span`
  display: inline-block;
  min-width: ${editableControlsWidth};
`;

const ControlButton = styled.span`
  display: inline-block;
  vertical-align: middle;

  &:hover {
    cursor: pointer;
  }
`;

const RemoveButton = styled(ControlButton)`
  font-size: 25px;
  margin-left: ${padding.small};
`;
